/** Small form building blocks for the public customer onboarding + privacy request pages. */
import { CODES } from "@/components/public/WhatsAppField";

export const inputCls = "w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-600/30 focus:border-sky-600 transition-colors";

export function Field({ label, required, hint, children, id }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[13px] font-semibold text-slate-800">{label}{required && <span className="text-rose-600"> *</span>}</label>
      {children}
      {hint && <p className="text-[11.5px] text-slate-500">{hint}</p>}
    </div>
  );
}

export function Text({ id, value, onChange, required, label, hint, testid, ...rest }) {
  return <Field id={id} label={label} required={required} hint={hint}><input id={id} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} /></Field>;
}

export function Select({ id, value, onChange, options, label, required, testid, placeholder = "Choose…" }) {
  return (
    <Field id={id} label={label} required={required}>
      <select id={id} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{placeholder}</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
}

export function Phone({ id, value, onChange, label, required, testid, hint }) {
  const v = value || { country_code: "+91", number: "" };
  return (
    <Field id={id} label={label} required={required} hint={hint}>
      <div className="flex gap-2">
        <select aria-label={`${label} country code`} className={`${inputCls} w-[118px] shrink-0`} value={v.country_code} onChange={(e) => onChange({ ...v, country_code: e.target.value })} data-testid={`${testid}-cc`}>
          {CODES.map(([c, l]) => <option key={c} value={c}>{c} {l}</option>)}
        </select>
        <input id={id} inputMode="tel" maxLength={20} className={inputCls} value={v.number} onChange={(e) => onChange({ ...v, number: e.target.value })} placeholder="Number" data-testid={`${testid}-number`} />
      </div>
    </Field>
  );
}

export function Address({ prefix, value, onChange, countries }) {
  const v = value || {};
  const set = (k) => (x) => onChange({ ...v, [k]: x });
  const india = v.country === "IN";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2"><Text id={`${prefix}-line1`} label="Address line 1" required value={v.line1} onChange={set("line1")} testid={`${prefix}-line1`} autoComplete="address-line1" /></div>
      <div className="sm:col-span-2"><Text id={`${prefix}-line2`} label="Address line 2" value={v.line2} onChange={set("line2")} testid={`${prefix}-line2`} autoComplete="address-line2" /></div>
      <Text id={`${prefix}-city`} label="City" required value={v.city} onChange={set("city")} testid={`${prefix}-city`} />
      <Text id={`${prefix}-state`} label="State / region" required={india} value={v.state} onChange={set("state")} testid={`${prefix}-state`} />
      <Text id={`${prefix}-postcode`} label={india ? "PIN code" : "Postcode"} required={india} value={v.postcode} onChange={set("postcode")} testid={`${prefix}-postcode`} inputMode={india ? "numeric" : "text"} maxLength={12} />
      <Select id={`${prefix}-country`} label="Country" required value={v.country} onChange={set("country")} options={countries} testid={`${prefix}-country`} />
    </div>
  );
}

export function Section({ n, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 shadow-[0_1px_0_rgba(15,23,42,0.04)]" data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="font-mono text-[11px] tracking-[0.2em] text-sky-700">{String(n).padStart(2, "0")}</span>
        <h2 className="text-base md:text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
